import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Switch,
  Table,
  Typography,
} from "@mui/joy";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useWorkspaceUserList } from "../../../services/api";
import type { WorkspaceUser } from "../../../common/types";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import { useGroups } from "../hooks/useGroups";

interface GroupMember {
  user_id: string;
}

const groupsApi = "/api/workspace/myvault/groups";

export default function GroupMembers() {
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";
  const {
    groups,
    loading: groupsLoading,
    error: groupsError,
    refresh: refreshGroups,
  } = useGroups(workspaceId);
  const listWorkspaceUsers = useWorkspaceUserList();
  const authFetch = useAuthFetch();
  const requestId = useRef(0);

  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDisplayName, setNewGroupDisplayName] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [groupId, setGroupId] = useState("");
  const [users, setUsers] = useState<WorkspaceUser[]>([]);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [applicationDataAllowed, setApplicationDataAllowed] = useState(false);
  const [applicationDataLoading, setApplicationDataLoading] = useState(false);
  const [applicationDataError, setApplicationDataError] = useState<
    string | null
  >(null);

  useEffect(() => {
    setGroupId((current) =>
      groups.some((group) => group.id === current)
        ? current
        : groups[0]?.id || "",
    );
  }, [groups]);

  useEffect(() => {
    if (!workspaceId) return;
    let active = true;
    listWorkspaceUsers(workspaceId)
      .then((workspaceUsers) => {
        if (active) {
          setUsers(workspaceUsers);
          setUsersError(null);
        }
      })
      .catch((error) => {
        if (active)
          setUsersError(
            error instanceof Error ? error.message : "Failed to load users",
          );
      });
    return () => {
      active = false;
    };
  }, [workspaceId, listWorkspaceUsers]);

  const loadMembers = useCallback(
    async (selectedGroupId: string) => {
      const currentRequest = ++requestId.current;
      setMembersLoading(true);
      setMembers([]);
      setMembersError(null);
      try {
        const response = await authFetch(
          `${groupsApi}/${selectedGroupId}/members`,
          "list group members",
          { headers: { "x-workspace-id": workspaceId } },
        );
        if (currentRequest === requestId.current) setMembers(response);
      } catch (error) {
        if (currentRequest === requestId.current)
          setMembersError(
            error instanceof Error ? error.message : "Failed to load members",
          );
      } finally {
        if (currentRequest === requestId.current) setMembersLoading(false);
      }
    },
    [authFetch, workspaceId],
  );

  useEffect(() => {
    setSelectedUserId(null);
    setSaveError(null);
    if (groupId) void loadMembers(groupId);
    else {
      ++requestId.current;
      setMembers([]);
      setMembersLoading(false);
    }
  }, [groupId, loadMembers]);

  useEffect(() => {
    if (!groupId) return;
    let active = true;
    setApplicationDataLoading(true);
    setApplicationDataError(null);
    authFetch(
      `${groupsApi}/${groupId}/application-data`,
      "load group application data",
      {
        headers: { "x-workspace-id": workspaceId },
      },
    )
      .then((result: { allowed: boolean }) => {
        if (active) setApplicationDataAllowed(result.allowed);
      })
      .catch((error) => {
        if (active)
          setApplicationDataError(
            error instanceof Error
              ? error.message
              : "Failed to load application data access",
          );
      })
      .finally(() => {
        if (active) setApplicationDataLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authFetch, groupId, workspaceId]);

  const updateApplicationData = async (allowed: boolean) => {
    if (!groupId) return;
    setApplicationDataLoading(true);
    setApplicationDataError(null);
    try {
      const result: { allowed: boolean } = await authFetch(
        `${groupsApi}/${groupId}/application-data`,
        "update group application data",
        {
          method: "PUT",
          headers: {
            "x-workspace-id": workspaceId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ allowed }),
        },
      );
      setApplicationDataAllowed(result.allowed);
    } catch (error) {
      setApplicationDataError(
        error instanceof Error
          ? error.message
          : "Failed to update application data access",
      );
    } finally {
      setApplicationDataLoading(false);
    }
  };

  const userById = useMemo(
    () => new Map(users.map((user) => [user.user_id, user])),
    [users],
  );
  const memberIds = useMemo(
    () => new Set(members.map((member) => member.user_id)),
    [members],
  );
  const availableUsers = users.filter(
    (user) => user.status === "accepted" && !memberIds.has(user.user_id),
  );

  const createGroup = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = newGroupName.trim();
    const displayName = newGroupDisplayName.trim();
    if (!workspaceId || !name || !displayName || creatingGroup) return;
    setCreatingGroup(true);
    setCreateError(null);
    try {
      const created: { id: string } = await authFetch(
        groupsApi,
        "create group",
        {
          method: "POST",
          headers: {
            "x-workspace-id": workspaceId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name, display_name: displayName }),
        },
      );
      await refreshGroups();
      setGroupId(created.id);
      setNewGroupName("");
      setNewGroupDisplayName("");
    } catch (error) {
      setCreateError(
        error instanceof Error ? error.message : "Failed to create group",
      );
    } finally {
      setCreatingGroup(false);
    }
  };

  const addMember = async () => {
    if (!groupId || !selectedUserId) return;
    setSaving(true);
    setSaveError(null);
    try {
      await authFetch(`${groupsApi}/${groupId}/members`, "add group member", {
        method: "POST",
        headers: {
          "x-workspace-id": workspaceId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: selectedUserId }),
      });
      setSelectedUserId(null);
      await loadMembers(groupId);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Failed to add member",
      );
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (userId: string) => {
    if (!groupId) return;
    setSaving(true);
    setSaveError(null);
    try {
      await authFetch(
        `${groupsApi}/${groupId}/members/${userId}`,
        "remove group member",
        {
          method: "DELETE",
          headers: { "x-workspace-id": workspaceId },
        },
      );
      await loadMembers(groupId);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Failed to remove member",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Typography level="h2" sx={{ mb: 1 }}>
        MyVault groups
      </Typography>
      <Typography level="body-sm" sx={{ mb: 3 }}>
        Assign accepted workspace users to application groups.
      </Typography>

      {groupsError && <Alert color="danger">{groupsError}</Alert>}
      {usersError && <Alert color="danger">{usersError}</Alert>}
      {saveError && <Alert color="danger">{saveError}</Alert>}

      <Box component="form" onSubmit={createGroup} sx={{ mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "end" }}
        >
          <FormControl>
            <FormLabel>Group name</FormLabel>
            <Input
              value={newGroupName}
              onChange={(event) => setNewGroupName(event.target.value)}
              disabled={creatingGroup}
              slotProps={{ input: { maxLength: 100 } }}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Display name</FormLabel>
            <Input
              value={newGroupDisplayName}
              onChange={(event) => setNewGroupDisplayName(event.target.value)}
              disabled={creatingGroup}
              slotProps={{ input: { maxLength: 255 } }}
            />
          </FormControl>
          <Button
            type="submit"
            loading={creatingGroup}
            disabled={
              !workspaceId ||
              !newGroupName.trim() ||
              !newGroupDisplayName.trim() ||
              creatingGroup
            }
          >
            Create group
          </Button>
        </Stack>
        {createError && (
          <Alert color="danger" sx={{ mt: 1 }}>
            {createError}
          </Alert>
        )}
      </Box>

      <FormControl sx={{ mb: 3, maxWidth: 400 }}>
        <FormLabel>Group</FormLabel>
        <Select
          value={groupId || null}
          placeholder={groupsLoading ? "Loading groups..." : "Select a group"}
          onChange={(_, value) => setGroupId(value || "")}
          disabled={groupsLoading || saving}
        >
          {groups.map((group) => (
            <Option key={group.id} value={group.id}>
              {group.display_name}
            </Option>
          ))}
        </Select>
      </FormControl>

      {!groupsLoading && groups.length === 0 && !groupsError && (
        <Typography>No MyVault groups are available.</Typography>
      )}

      {groupId && (
        <>
          <FormControl sx={{ mb: 3 }}>
            <FormLabel>Application data access</FormLabel>
            <Switch
              checked={applicationDataAllowed}
              onChange={(event) =>
                void updateApplicationData(event.target.checked)
              }
              disabled={applicationDataLoading || !!applicationDataError}
              slotProps={{ input: { "aria-label": "Allow application data" } }}
            />
            {applicationDataError && (
              <Alert color="danger">{applicationDataError}</Alert>
            )}
          </FormControl>
          <Stack direction="row" spacing={2} alignItems="end" sx={{ mb: 3 }}>
            <FormControl sx={{ minWidth: 280 }}>
              <FormLabel>Workspace user</FormLabel>
              <Select
                value={selectedUserId}
                placeholder="Select an accepted user"
                onChange={(_, value) => setSelectedUserId(value)}
                disabled={saving || membersLoading || !!usersError}
              >
                {availableUsers.map((user) => (
                  <Option key={user.user_id} value={user.user_id}>
                    {user.email}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <Button onClick={addMember} disabled={!selectedUserId || saving}>
              Add member
            </Button>
          </Stack>

          {membersError && <Alert color="danger">{membersError}</Alert>}
          {membersLoading ? (
            <CircularProgress aria-label="Loading group members" />
          ) : members.length === 0 ? (
            <Typography>This group has no members.</Typography>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th style={{ width: 120 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.user_id}>
                    <td>
                      {userById.get(member.user_id)?.email || member.user_id}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        color="danger"
                        variant="plain"
                        disabled={saving}
                        onClick={() => removeMember(member.user_id)}
                        aria-label={`Remove ${userById.get(member.user_id)?.email || member.user_id}`}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </Box>
  );
}
